import type { ChatMessageButtons } from '../types/chatMessageButtonTypes';

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const label = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

export function chatMessageButtons(metadata: unknown): ChatMessageButtons | null {
  const data = asRecord(metadata);
  if (!data) return null;

  const interactive = asRecord(data.interactive);
  if (interactive?.kind === 'urlButton') {
    const displayText = label(interactive.displayText);
    return displayText ? { buttons: [{ label: displayText }] } : null;
  }

  if (interactive?.kind === 'buttons' && Array.isArray(interactive.buttons)) {
    const buttons = interactive.buttons.flatMap((value: unknown) => {
      const title = label(asRecord(value)?.title);
      return title ? [{ label: title }] : [];
    });
    return buttons.length ? { buttons } : null;
  }

  if (interactive?.kind === 'list' && Array.isArray(interactive.sections)) {
    const buttons = interactive.sections.flatMap((value: unknown) => {
      const section = asRecord(value);
      if (!Array.isArray(section?.rows)) return [];
      return section.rows.flatMap((rowValue: unknown) => {
        const row = asRecord(rowValue);
        const title = label(row?.title);
        return title ? [{ label: title, detail: label(row?.description) || undefined }] : [];
      });
    });
    return buttons.length
      ? { title: label(interactive.listButtonTitle) || undefined, buttons }
      : null;
  }

  if (!Array.isArray(data.buttons)) return null;
  const buttons = data.buttons.flatMap((value: unknown) => {
    const button = asRecord(value);
    const type = label(button?.type);
    if (!['QUICK_REPLY', 'URL', 'PHONE_NUMBER'].includes(type)) return [];
    const text = label(button?.text);
    if (type === 'QUICK_REPLY' && /^quick_reply_\d+$/.test(text)) return [];
    return text ? [{ label: text }] : [];
  });
  return buttons.length ? { buttons } : null;
}
